import { Injectable } from "@angular/core";
import { ActivityActorEvent } from "../../model/activityActor-event";
import { ToastrService } from 'ngx-toastr';
import { Actor } from "../../model/actor";
import { Activity } from "../../model/activity";
import { ActionResult } from "../../model/actionResult";
import { ConfirmDialog } from "../../confirmation/confirm.dialog";
import { MatDialogRef } from '@angular/material/dialog';
import { SettingsService } from "../../services/settings.service";
import { ActivityActorService } from "../../services/activityActor.service";
import { LiveFlightService } from "../../services/liveFlight.service";
import { BfsService } from "../../services/bfs.service";

@Injectable()
export class SchedulerUpperService {

  confirmModalRef: MatDialogRef<ConfirmDialog>;

  constructor(
    private readonly liveFlightService: LiveFlightService,
    private readonly toastr: ToastrService,
    private readonly settingsService: SettingsService,
    private readonly activityActorService: ActivityActorService,
    private readonly bfsService: BfsService
  ) { }

  showError(message: string) {
    this.toastr.error(message);
  }

  showInfo(message: string) {
    this.toastr.info(message);
  }

  /// service calls

  async getAllowedEvents(activity: Activity): Promise<AllowedEvents> {
    let allowedEventList = await this.getAllowedEventsInternal(activity);
    return new AllowedEvents(allowedEventList);
  }

  private async getAllowedEventsInternal(activity: Activity): Promise<any> {
    return await this.activityActorService.getAllowedEvents(activity)
      .then((allowedEventList: any) => {
        return allowedEventList;
      })
      .catch(err => {
        console.log(err);
        this.showError(err);
      });
  }

  /// actor
  async updateActor(actor: Actor): Promise<boolean> {
    return await this.activityActorService.updateActor(actor).then(reply => {
      return reply;
    },
      (error) => {
        this.showError("Ett fel uppstod vid uppdateringen!");
        console.log("Something went wrong while updating activity:", error);
        return false;
      });
  }
  /// actor - end

  /// state & activity

  async cancelActivity(actor: Actor, activity: Activity): Promise<Activity> {
    return await this.updateStateInternal("Cancel", actor, activity).then(result => {
      this.showInfo('Uppdraget har avbrutits.');
      return result;
    }, (error) => {
      console.log(error);
      this.showError('Något gick fel med statusändringen!');
    });
  }

  async copyActivity(actor: Actor, sourceActivityId: string, targetAvailabilityId: string): Promise<any> {
    return await this.activityActorService.copyActivity(actor, sourceActivityId, targetAvailabilityId)
      .then((result: ActionResult) => { },
        (error) => {
          this.showError('Operationen kunde inte utföras.');
          console.log(error);
        });
  }

  async reAssignActivity(currentActivity: Activity, newActivity: Activity): Promise<Activity> {
    return await this.activityActorService.reAssignActivity(currentActivity, newActivity).then(result => {
      this.showInfo('Uppdraget har blivit omplanerat.');
      return result;
    }, (error) => {
      console.log(error);
      this.showError('Något gick fel med omplanering av uppdrag!');
    });
  }

  async updateState(status: string, actor: Actor, activity: Activity): Promise<Activity> {
    return await this.updateStateInternal(status, actor, activity)
      .then(result => {
        this.showInfo('Uppdraget har ändrat status.');
        return result;
      }, (error) => {
        console.log(error);
        this.showError('Något gick fel med statusändringen!');
        return undefined;
      });
  }

  private async updateStateInternal(status: string, actor: Actor, activity: Activity): Promise<any> {
    return this.activityActorService.updateState(status, actor, activity)
  }

  async unassingActivity(actor: Actor, activity: Activity): Promise<boolean> {
    return await this.updateStateInternal("UnAssign", actor, activity)
      .then(result => {
        if (result == null) {
          this.showError('Något gick fel vid avbokningen av uppdraget, ladda om sidan och försök igen!');
          return false;
        }
        this.showInfo('Uppdraget har avbokats.');
        return true;
      }, (error) => {
        this.showError('Något gick fel med avbokning av uppdrag');
        console.log('Något gick fel med avbokning av uppdrag: ' + error.error);
        return false;
      });;
  }

  async updateActivity(activity: Activity): Promise<Activity> {
    return await this.activityActorService.updateActivity(activity)
      .then(reply => {
        this.showInfo('Uppdraget har blivit uppdaterat.');
        return reply;
      }, (error) => {
        this.showError("Ett fel uppstod vid uppdateringen!");
        console.log("Something went wrong while updating activity:", error);
        return undefined;
      });
  }

  /// state & activity - end

  /// transfer
  async changeEta(event: ActivityActorEvent, min: string): Promise<boolean> {
    return await this.bfsService.updateTransferEta(event.activityActor.actorID, min)
      .then(reply => {
        this.showInfo('Transfern har uppdaterats.');
        return reply;
      }, (error) => {
        this.showError("Ett fel uppstod vid uppdateringen!");
        console.log("Something went wrong while updating transfer eta:", error);
      });
  }

  async updateTransferEta(actorID: string, min: string): Promise<boolean> {
    return await this.bfsService.updateTransferEta(actorID, min)
      .then(reply => {
        this.showInfo('Transfern har uppdaterats.');
        return reply;
      }, (error) => {
        this.showError("Ett fel uppstod vid uppdateringen!");
        console.log("Something went wrong while updating transfer eta:", error);
      });
  }

  async confirmNoShow(activityID: string): Promise<any> {
    return await this.activityActorService.confirmNoShow(activityID)
      .then(() => {
        this.showInfo('BOM bekräftad');
      },
        (error) => {
          console.log(error);
          this.showError('Det gick ej att bekräfta BOM');
        });
  }

  // transfer - end

  // liveFlight
  async ackFlight(liveFlightID: string): Promise<any> {
    return await this.liveFlightService.ackFlight(liveFlightID)
      .then(result => {
        this.showInfo('Förändring bekräftad');
      },
        (error) => {
          console.log(error);
          this.showError('Could not ack flight');
        });
  }
  // lifeFlight - end


  getCurrentAirport() {
    return this.settingsService.getCurrentAirport();
  }

  /// service calls end



}

export class AllowedEvents {

  private readonly unassign: boolean = false;
  private readonly assign: boolean = true;
  private readonly accept: boolean = true;
  private readonly cancel: boolean = false;
  private readonly final: boolean = true;
  private readonly allowedEvents: string[];

  constructor(events: string[]) {
    if (events) {
      this.allowedEvents = events;
      this.unassign = events.indexOf('UnAssign') >= 0;
      this.accept = events.indexOf('Accept') >= 0;
      this.cancel = events.indexOf('Cancel') >= 0;
      this.final = events.indexOf('Final') >= 0;
      this.assign = events.indexOf('Assign') >= 0;
    }
  }

  UnAssign(): boolean {
    return this.unassign;
  }

  Accept(): boolean {
    return this.accept;
  }

  Cancel(): boolean {
    return this.cancel;
  }

  Final(): boolean {
    return this.final;
  }

  Assign(): boolean {
    return this.assign;
  }

  ToList(): string[] {
    return this.allowedEvents;
  }
}
